import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { PageHeader, EmptyState, LiveChip } from "@/components/shared";
import { useLiveEvents } from "@/hooks/useLiveEvents";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { ListChecks, RefreshCw, XCircle } from "lucide-react";

const STATUS_COLOR = {
  QUEUED: "text-slate-400", RUNNING: "text-sky-500", RETRYING: "text-amber-500",
  COMPLETED: "text-emerald-500", PARTIAL: "text-amber-500", FAILED: "text-rose-500", CANCELLED: "text-slate-500",
};
const ACTIVE = ["QUEUED", "RUNNING", "RETRYING"];

export default function Jobs() {
  const [items, setItems] = useState(null);
  const load = useCallback(
    () => api.get("/jobs").then((r) => setItems(r.data.items)).catch(() => setItems([])),
    []
  );
  useEffect(() => { load(); }, [load]);

  // Live updates replace the old 3-second poll: the server pushes each job
  // transition, so the list stays current without a request loop.
  useLiveEvents(["job.created", "job.updated"], (event) => {
    const job = event?.data?.job;
    if (!job) return;
    setItems((prev) => {
      const list = prev || [];
      const idx = list.findIndex((j) => j.id === job.id);
      if (idx === -1) return [job, ...list].slice(0, 100);
      const next = [...list];
      next[idx] = { ...next[idx], ...job };
      return next;
    });
  });

  const cancel = (id) => api.post(`/jobs/${id}/cancel`).catch(() => {});

  return (
    <div className="space-y-5">
      <PageHeader
        title="Jobs"
        desc="Background operations — broadcasts, bulk actions, provisioning. Claimed by exactly one worker."
        actions={
          <div className="flex items-center gap-2">
            <LiveChip />
            <Button size="sm" variant="outline" onClick={load} className="gap-1.5" data-testid="btn-refresh-jobs">
              <RefreshCw size={14} /> Refresh
            </Button>
          </div>
        }
        testId="jobs-header"
      />
      {!items ? <Skeleton className="h-40 w-full" /> : items.length === 0 ? (
        <div className="bg-card border border-border rounded-lg"><EmptyState icon={ListChecks} title="No jobs yet" desc="Bulk actions and broadcasts will appear here with live progress." testId="empty-jobs" /></div>
      ) : (
        <div className="space-y-3">
          {items.map((j) => (
            <div key={j.id} className="bg-card border border-border rounded-lg p-4" data-testid={`job-${j.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div><div className="font-medium text-sm">{j.title}</div><div className="text-xs text-muted-foreground mono">{j.type}</div></div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold ${STATUS_COLOR[j.status]}`} data-testid={`job-status-${j.id}`}>{j.status}</span>
                  {ACTIVE.includes(j.status) && (
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-rose-500"
                      onClick={() => cancel(j.id)} data-testid={`btn-cancel-job-${j.id}`} title="Cancel job"><XCircle size={14} /></Button>
                  )}
                </div>
              </div>
              <Progress value={(j.progress / Math.max(j.total, 1)) * 100} className={`h-1.5 ${j.status === "FAILED" ? "[&>*]:bg-rose-500" : j.status === "PARTIAL" || j.status === "RETRYING" ? "[&>*]:bg-amber-500" : ""}`} />
              <div className="flex items-center justify-between text-xs text-muted-foreground mt-1.5">
                <span className="mono">{j.progress}/{j.total}{j.attempt > 1 ? ` · attempt ${j.attempt}` : ""}</span>
                <span>{new Date(j.created_at).toLocaleString()}</span>
              </div>
              {j.status === "RETRYING" && j.next_attempt_at && (
                <div className="text-xs text-amber-500 mt-1" data-testid={`job-retry-${j.id}`}>
                  Waiting out Telegram's rate limit — next attempt {new Date(j.next_attempt_at).toLocaleTimeString()}
                </div>
              )}
              {j.result && <div className="text-xs mono text-muted-foreground mt-1">{JSON.stringify(j.result)}</div>}
              {j.error && <div className="text-xs text-rose-500 mt-1">{j.error}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
